import React, { PropsWithChildren, useMemo } from "react";
import { Config } from "./Config";
import { DateType, PlaylistThumbLinkType } from "@kaltura/mediaspace-shared-types";
import SetLocalCodeHelper from "./SetLocalCodeHelper";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";

dayjs.extend(localeData);
dayjs.extend(updateLocale);

export const defaultContext: Config = {
    application: {
        partnerId: 0,
        serviceUrl: "https://www.kaltura.com",
        cdnUrl: "https://cdnapisec.kaltura.com",
        isLoggedIn: false,
        userRole: "emptyRole",
        isKAF: false,
        profileName: "MediaSpace",
        enableLoadButton: false,
        dateType: DateType.TIME_AGO,
        enableEntryTitles: false,
        timeDisplay: "am-pm",
        currentLocaleCode: "en",
        shortDateFormat: "MM/DD/YYYY",
        dateFormats: {
            longDate: "dddd, MMMM DD, YYYY",
            shortDate: "MMMM DD, YYYY",
            longDateNoYear: "dddd, MMMM DD",
        },
        weekStartDay: 0,
        playlistThumbLink: PlaylistThumbLinkType.playlistPage,
        unisphereWorkspaceUrl: "https://unisphere.nvq2.ovp.kaltura.com/v1",
        shouldCheckAsyncJobsStatus: false,
        enableUnlisted: true,
        cspNonce: "random",
        loadThumbnailWithKs: false,
        useUserTimezone: false,
    },
    calendar: {
        getAppointmentUrl: "/default/calendar/get-calendar-appointment",
    },
    watchlist: {
        localStorageExpiry: 5,
    },
    passwordValidations: {
        noRules: false,
        validations: {
            rule1: {
                regex: "^.{8,14}$",
                description: "Must be of length between 8 and 14",
            },
            rule2: {
                regex: "[0-9]+",
                description: "Must contain at least one digit (0-9)",
            },
            rule3: {
                regex: "[a-z]+",
                description: "Must contain at least one lowercase letter (a-z)",
            },
            rule4: {
                regex: "[~!@#$%^*=+?}\\(\\)\\-\\[\\]\\{\\}]+",
                description: "Must contain at least one of the following symbols:  %%~!@#$^*=+?[]{}",
            },
            rule5: {
                regex: "^[^<>]*$",
                description: "Must not contain the following characters: < or >",
            },
        },
    },
    siteRegistration: {
        editor: {
            fieldTypeOptions: [],
        },
    },
    dsLayout: {
        components: {},
        headerColor: "dark",
    },
    eventPlatform: {
        eventListItem: {
            showThumbnail: false,
            showPresenters: true,
            showPresenterModal: false,
            joinBuffer: 15,
            isPresenterClickable: true,
        },
    },
    analytics: {
        serviceUrl: "analytics.nvq2.ovp.kaltura.com",
        appType: 1,
        appName: "",
        analyticsContext: "",
    },
    categories: {
        rootGalleryCategoryId: "",
    },
};

export const ConfigContext: React.Context<Config> = React.createContext(defaultContext);

export interface ConfigProviderProps {
    context?: Config;
}

/**
 * This component makes the `config` available down the React tree.
 * It should preferably be used at **the root of your component tree**.
 */
export const ConfigProvider = (props: PropsWithChildren<ConfigProviderProps>) => {
    const { children, context: config } = props;

    const currentLocaleCode = config?.application.currentLocaleCode;
    const weekStartDay = config?.application.weekStartDay;

    useMemo(() => {
        SetLocalCodeHelper.setLocalLanguage(currentLocaleCode);
        dayjs.updateLocale(
            currentLocaleCode ?? "en",
            {
                weekStart: weekStartDay ?? 0,
            }
        );
    }, [currentLocaleCode, weekStartDay]);

    return (
        <ConfigContext.Provider value={typeof config === "object" ? { ...defaultContext, ...config } : defaultContext}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocaleCode}>
                {children}
            </LocalizationProvider>
        </ConfigContext.Provider>
    );
};

export default ConfigProvider;
